import React, { useState } from "react";
import { ChevronDown, GitBranch, Wallet, Zap, ShieldCheck, X } from "lucide-react";

/**
 * Plain-English routing explainer — used on /admin/routing, /admin/campaigns,
 * and /dashboard/campaign. Subtle navy/slate, no traffic-light colours, sharp
 * edges. Collapsible so it doesn't get in the way once the user understands.
 *
 * `audience`: "admin" | "client"
 */
export default function RoutingExplainer({ audience = "admin", storageKey }) {
  const sKey = storageKey || `routing-explainer-${audience}`;
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(sKey) !== "collapsed"; } catch { return true; }
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(sKey, next ? "open" : "collapsed"); } catch {}
  };

  const steps = audience === "admin" ? STEPS_ADMIN : STEPS_CLIENT;
  const title = audience === "admin" ? "How routing works" : "How your campaign works";
  const sub   = audience === "admin"
    ? "The path a single lead takes from publisher to wallet."
    : "Every lead routed to your wallet follows the same five steps.";

  return (
    <div
      data-testid="routing-explainer"
      className="rounded-md border border-slate-200 bg-gradient-to-br from-slate-50 to-white"
    >
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        data-testid="routing-explainer-toggle"
      >
        <div className="flex items-center gap-2.5">
          <span className="inline-flex size-7 items-center justify-center rounded-sm bg-slate-900 text-white">
            <GitBranch className="size-3.5" />
          </span>
          <div>
            <div className="text-sm font-semibold text-slate-900 leading-tight">{title}</div>
            <div className="text-xs text-slate-500 leading-tight mt-0.5">{sub}</div>
          </div>
        </div>
        <ChevronDown className={`size-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-slate-200 px-4 py-4">
          <ol className="space-y-3">
            {steps.map((s, i) => (
              <li key={i} className="flex gap-3" data-testid={`routing-step-${i+1}`}>
                <span className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white text-[11px] font-semibold text-slate-700 tabular-nums">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                    <s.Icon className="size-3.5 text-slate-500" />
                    {s.label}
                  </div>
                  <div className="text-[13px] text-slate-600 leading-snug mt-0.5">{s.body}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

const STEPS_ADMIN = [
  {
    Icon: Zap,
    label: "Publisher posts a lead",
    body: "A vetted publisher hits POST /api/v1/leads/ingest with sector + consumer details and their pk_live API key. Sandbox keys never reach this stage.",
  },
  {
    Icon: GitBranch,
    label: "Sector → live campaign match",
    body: "Connexis finds the most recent live campaign for that sector and takes the campaign owner as the buyer. If no campaign exists, falls back FIFO to a paid lead-pack for that sector.",
  },
  {
    Icon: ShieldCheck,
    label: "Buyer eligibility checks",
    body: "Delivery-paused, outside business hours, or daily-cap-reached → lead is rejected with a clear reason code. Nothing is charged.",
  },
  {
    Icon: Wallet,
    label: "Wallet or lead-pack debit",
    body: "If an active lead-pack exists for that sector → decrement quantity_delivered. Otherwise → debit wallet at the sector's CPL. Both paths produce an audit-trail event.",
  },
  {
    Icon: Zap,
    label: "Delivery + Auto Follow-Up",
    body: "Lead is sent to the buyer's webhook (and/or shown in their dashboard). Auto Follow-Up fires the buyer's SMS + email template within seconds.",
  },
];

const STEPS_CLIENT = [
  {
    Icon: Zap,
    label: "Publisher sends a matching lead",
    body: "A vetted Connexis publisher sources a real consumer who fits your sector — and posts them through our compliance and consent pipeline.",
  },
  {
    Icon: GitBranch,
    label: "Routed to your campaign",
    body: "Sector match → your live campaign → that's you. No auction, no waiting. The first eligible lead in your sector is yours.",
  },
  {
    Icon: ShieldCheck,
    label: "Your guardrails apply",
    body: "Outside your business hours, daily cap reached, or you've paused delivery? The lead is rejected before it touches your wallet.",
  },
  {
    Icon: Wallet,
    label: "Your wallet debits at the sector CPL",
    body: "If you've prepaid a lead-pack for this sector it's used first (FIFO). Otherwise the lead's cost-per-lead is debited from your wallet balance.",
  },
  {
    Icon: Zap,
    label: "You receive it instantly",
    body: "Webhook to your CRM in <60s, visible in /dashboard/leads, and Auto Follow-Up fires your branded SMS + email to the lead within seconds.",
  },
];
